/**
 * Future Probability™ — Layer 5, rendered verbatim from the engine.
 *
 * Rules honoured here:
 *  - Never show a raw score without saying why → every gauge carries its
 *    explanation and its component breakdown.
 *  - Missing evidence is neutral 50 and flagged, never zero → flagged
 *    components are labelled "no evidence yet", not shown as a 0.
 */

import { motion } from "framer-motion";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import type { ProbabilityIndicator } from "@wcc360/engine/skills-engine";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export interface FutureProbabilityPanelProps {
  indicators: ProbabilityIndicator[];
  loading?: boolean;
  /** 0–1 share of the evidence bank answered. */
  coverage?: number;
  onRefresh?: () => void | Promise<unknown>;
  refreshing?: boolean;
  /** Compact mode drops the heading — used inside the Vision Board. */
  embedded?: boolean;
}

function Gauge({ value, flagged }: { value: number; flagged: boolean }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative mx-auto w-24 h-24">
      <svg viewBox="0 0 100 100" className="-rotate-90" aria-hidden>
        <circle cx="50" cy="50" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="8" opacity="0.35" />
        <circle
          cx="50" cy="50" r={r} fill="none"
          stroke={flagged ? "hsl(var(--muted-foreground))" : "hsl(var(--primary))"}
          strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - value / 100)}
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display text-2xl font-bold">
        {value}
      </div>
    </div>
  );
}

export function FutureProbabilityPanel({
  indicators, loading, coverage = 0, onRefresh, refreshing, embedded,
}: FutureProbabilityPanelProps) {
  const empty = !loading && indicators.length === 0;

  return (
    <section aria-labelledby="fp-heading" className="space-y-6">
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-display uppercase tracking-[0.35em] text-primary/80">
              Layer 5 · Future Probability™
            </div>
            <h2 id="fp-heading" className="mt-3 font-display text-2xl sm:text-3xl font-bold">
              Five living indicators
            </h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Not grades. They breathe as you add evidence, finish steps and keep your streak.
            </p>
          </div>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={() => void onRefresh()} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading your evidence…
        </div>
      )}

      {empty && (
        <div className="rounded-3xl border border-border bg-card/60 p-6">
          <p className="font-display text-lg">Nothing to show yet — and that's fine.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Answer a few evidence questions and all five indicators appear, each with the reason
            behind it. We never guess a number for you.
          </p>
        </div>
      )}

      {!!indicators.length && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {indicators.map((ind, i) => {
              const lowEvidence = ind.confidence < 0.4;
              return (
                <motion.div
                  key={ind.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="rounded-3xl border border-border bg-card/60 p-5 text-center"
                >
                  <Gauge value={ind.value} flagged={lowEvidence} />
                  <div className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">
                    {ind.label}
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Confidence {Math.round(ind.confidence * 100)}%
                  </div>
                  {lowEvidence && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      <AlertCircle className="h-3 w-3" aria-hidden /> Needs more evidence
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          <Accordion type="single" collapsible className="rounded-3xl border border-border bg-card/40 px-4">
            {indicators.map((ind) => (
              <AccordionItem key={ind.key} value={ind.key}>
                <AccordionTrigger className="text-left">
                  <span className="font-medium">Why {ind.label} is {ind.value}</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">{ind.explanation}</p>
                  <ul className="space-y-3">
                    {ind.components.map((c) => (
                      <li key={c.key}>
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                          <span>{c.label}</span>
                          <span className="text-muted-foreground text-xs">
                            {c.flagged ? "no evidence yet — held at neutral" : `${c.value}`}
                            {" · "}{Math.round(c.weight * 100)}% of this score
                          </span>
                        </div>
                        <Progress
                          value={c.value ?? 50}
                          className={`mt-1.5 h-1.5 ${c.flagged ? "opacity-40" : ""}`}
                          aria-label={`${c.label} contribution`}
                        />
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <p className="text-xs text-muted-foreground">
            Based on {Math.round(coverage * 100)}% of your evidence questions. Every gap is held at
            neutral, never counted as zero.
          </p>
        </>
      )}
    </section>
  );
}
